import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import RecordRTC, { StereoAudioRecorder } from "recordrtc";
import blinkscribeapi from "../api/blinkscribeapi";
import { saveAudioInIndexDB } from "../offlinestreaming/offlineBlobHandler";
import { generateFilename } from "../utils/generatefilename";
import { eventBus } from "../utils/eventBus";

const RecordingContext = createContext(null);

export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (!context) {
    throw new Error("useRecording must be used within a RecordingProvider");
  }
  return context;
};

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = (error) => reject(error);
  });
};

export const RecordingProvider = ({ children }) => {
  // === UI State ===
  const [isGlobalRecording, setIsGlobalRecording] = useState(false);
  const [isGlobalPaused, setIsGlobalPaused] = useState(false);
  const [globalElapsedTime, setGlobalElapsedTime] = useState(0);
  const [patientInfo, setPatientInfo] = useState(null);
  const [showFloatingPopup, setShowFloatingPopup] = useState(false);
  const [isOnRecordingPage, setIsOnRecordingPage] = useState(false);

  // === Live Scribe State (Passive Scribe — progressive SOAP via Gemini Live) ===
  const [liveSOAPNote, setLiveSOAPNote] = useState(null);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [suggestedICD10, setSuggestedICD10] = useState([]);
  const [suggestedOrders, setSuggestedOrders] = useState([]);
  const [careIQAlerts, setCareIQAlerts] = useState([]);
  const [toolCallCount, setToolCallCount] = useState(0);
  const [liveScribeHealthy, setLiveScribeHealthy] = useState(true);
  const liveScribeActivityRef = useRef(null); // tracks last WS activity

  // Server-pushed recording status (resilience signals from backend).
  // Shape: { severity: "warning"|"error"|"info", code: string, message: string, ts: number }
  const [recordingStatus, setRecordingStatus] = useState(null);
  // 45-min "are you still recording?" prompt.
  const [showLongRecordingPrompt, setShowLongRecordingPrompt] = useState(false);
  const lastLongPromptAtRef = useRef(0); // ms timestamp of the last prompt firing

  // === Post-recording processing state ===
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState(""); // "Transcribing", "Generating Note", "Complete"
  const [processingPercent, setProcessingPercent] = useState(0);
  const [lastSOAPNote, setLastSOAPNote] = useState(null);
  const [lastTranscript, setLastTranscript] = useState("");
  const [lastNotePatientId, setLastNotePatientId] = useState(null);
  const processingTimerRef = useRef(null);

  // === Recording Infrastructure Refs (survive navigation) ===
  const wsRef = useRef(null);
  const recorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const pausedRef = useRef(false);
  const isManualStopRef = useRef(false);
  const isManualRecordStartRef = useRef(false);
  const blockerRef = useRef(false);
  const combineStatusRef = useRef(false);
  const latestAppRef = useRef("");
  const streamNameRef = useRef("");
  const streamFilenameRef = useRef("");
  const userRef = useRef("");
  const patientDataRef = useRef(null);
  const pendingChunksRef = useRef([]);  // Fix 3: Buffer for audio chunks during reconnection
  const heartbeatRef = useRef(null);    // Fix 5: Heartbeat interval ref
  const wsQueryStringRef = useRef("");  // Store query string for reconnection

  // Manual recording callbacks (set by SideFrame's useAudioRecorder)
  const manualRecordRef = useRef(null);
  const stopManualRecordingRef = useRef(null);
  const pauseManualRecordingRef = useRef(null);
  const resumeManualRecordingRef = useRef(null);

  // Callback for poll trigger (SideFrame-specific)
  const setPollTriggerRef = useRef(null);

  // Toast callbacks (set by whoever mounts the component)
  const showToastRef = useRef(null);

  // === Prevent accidental tab/browser close while recording ===
  useEffect(() => {
    const handler = (e) => {
      if (isGlobalRecording) {
        e.preventDefault();
        e.returnValue = "A recording is in progress. If you leave, the recording may be lost.";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isGlobalRecording]);

  // === Timer Functions ===
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now() - (globalElapsedTime || 0) * 1000;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const timePassed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setGlobalElapsedTime(timePassed);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  // Tracks whether the provider has used Pause at any point during this
  // recording. Used by the short-recording stop confirmation: if the
  // provider deliberately paused & resumed they're intentionally controlling
  // timing, so we don't second-guess a short stop.
  const usedPauseRef = useRef(false);

  // === Core Recording Functions ===
  const togglePause = useCallback(() => {
    if (isGlobalPaused) {
      // Resume
      pausedRef.current = false;
      if (isManualRecordStartRef.current && resumeManualRecordingRef.current) {
        resumeManualRecordingRef.current();
      }
      startTimeRef.current = Date.now() - globalElapsedTime * 1000;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        const timePassed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setGlobalElapsedTime(timePassed);
      }, 1000);
      setIsGlobalPaused(false);
    } else {
      // Pause
      pausedRef.current = true;
      usedPauseRef.current = true;
      if (isManualRecordStartRef.current && pauseManualRecordingRef.current) {
        pauseManualRecordingRef.current();
      }
      stopTimer();
      setIsGlobalPaused(true);
    }
  }, [isGlobalPaused, globalElapsedTime, stopTimer]);

  const stopRecording = useCallback(() => {
    isManualStopRef.current = true;
    isManualRecordStartRef.current = false;

    if (recorderRef.current) {
      recorderRef.current.stopRecording(() => {
        recorderRef.current = null;
      });
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, "Recording stopped by user");
    }

    // Clean up heartbeat and pending chunks
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    pendingChunksRef.current = [];

    stopTimer();
    setGlobalElapsedTime(0);
    setIsGlobalRecording(false);
    setIsGlobalPaused(false);
    setShowFloatingPopup(false);
    setPatientInfo(null);

    // Reset live scribe state (but NOT lastSOAP/lastTranscript)
    // Preserve suggestedICD10 and suggestedOrders so CareIQ tab keeps showing them
    setLiveSOAPNote(null);
    setLiveTranscript("");
    setCareIQAlerts([]);
    setToolCallCount(0);

    setTimeout(() => {
      eventBus.emit("refreshDashboard");
    }, 6000);
  }, [stopTimer]);

  const onSave = useCallback(() => {
    if (isManualRecordStartRef.current && stopManualRecordingRef.current) {
      stopManualRecordingRef.current();
    }

    if (showToastRef.current) {
      showToastRef.current("Recording Saved Successfully", "success");
    }

    // Preserve SOAP data before stopRecording clears it
    setLastSOAPNote((prev) => liveSOAPNote || prev);
    setLastTranscript((prev) => liveTranscript || prev);

    // Capture which patient this note belongs to BEFORE stopRecording clears patientInfo
    setLastNotePatientId(patientDataRef.current?.patientid || null);

    // Start processing indicator
    setIsProcessing(true);
    setProcessingStage("Transcribing");
    setProcessingPercent(10);

    stopRecording();

    // For offline / manual-recording flow the audio sits in IndexedDB and
    // only uploads on a window 'online' event or full app reload — neither
    // happens automatically when the user just clicks Save while still in
    // the same session. Kick the uploader directly so the backend recording
    // row gets created and the poll below has something to find.
    try {
      if (isManualRecordStartRef.current) {
        // Lazy import to avoid circular dependency at module load
        import("../offlinestreaming/uploadrecordings")
          .then(({ UploadindexdbRecording }) => UploadindexdbRecording())
          .catch(() => { /* non-fatal */ });
      }
    } catch (_) { /* non-fatal */ }

    // Simulated processing stages with timed progression
    // Stage 1: Transcribing (10% → 45%) over ~8s
    // We cap at 88 — leaves a clear visual gap so users know there's more
    // work pending, and the final ~12% is driven by the actual completion
    // poll below (or the safety timeout). The previous 95% cap led to
    // "stuck at 90/95%" reports because users couldn't tell whether the
    // bar was moving or hung.
    let pct = 10;
    if (processingTimerRef.current) clearInterval(processingTimerRef.current);
    processingTimerRef.current = setInterval(() => {
      pct += 5;
      if (pct >= 45) {
        setProcessingStage("Generating Note");
      }
      if (pct >= 85) {
        setProcessingStage("Finalizing");
      }
      if (pct >= 88) {
        pct = 88;
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
      setProcessingPercent(pct);
    }, 1500);

    // Poll for actual completion via backend (checks transcriptionstatus)
    const patientId = patientDataRef.current?.patientid;
    if (patientId) {
      const pollInterval = setInterval(async () => {
        try {
          const resp = await blinkscribeapi.post(
            `/v1/patient/${patientId}/recording/details`,
            { filter_array: { date_filter: { start_date: new Date().toISOString().slice(0, 10), end_date: new Date().toISOString().slice(0, 10) } } }
          );
          const records = resp?.data || [];
          // Find the latest record — check if processing is done
          const latest = records.sort((a, b) => new Date(b.ts) - new Date(a.ts))[0];
          if (latest && latest.transcriptionstatus !== "Processing") {
            // Done! Update to 100%
            clearInterval(pollInterval);
            if (processingTimerRef.current) {
              clearInterval(processingTimerRef.current);
              processingTimerRef.current = null;
            }
            setProcessingPercent(100);
            setProcessingStage("Complete");
            // Keep isProcessing true for a moment so UI shows 100%
            setTimeout(() => {
              setIsProcessing(false);
              eventBus.emit("refreshDashboard");
            }, 1500);
          }
        } catch (e) {
          // Polling error — non-blocking
        }
      }, 4000);

      // Safety timeout: stop polling after 2 minutes. Long recordings can
      // take ~60-90s to summarize, so 120s gives a generous margin without
      // leaving the bar stuck for several minutes when the poll lookup
      // can't find the row (e.g., the record was created with a non-today
      // timestamp, or the offline upload silently dropped). After this
      // fires we force 100% and clear the processing UI.
      setTimeout(() => {
        clearInterval(pollInterval);
        if (processingTimerRef.current) {
          clearInterval(processingTimerRef.current);
          processingTimerRef.current = null;
        }
        setProcessingPercent(100);
        setProcessingStage("Complete");
        setTimeout(() => setIsProcessing(false), 1500);
      }, 120000);
    } else {
      // No patient ID — just run the simulated timer to completion
      setTimeout(() => {
        if (processingTimerRef.current) {
          clearInterval(processingTimerRef.current);
          processingTimerRef.current = null;
        }
        setProcessingPercent(100);
        setProcessingStage("Complete");
        setTimeout(() => setIsProcessing(false), 1500);
      }, 30000);
    }

    if (setPollTriggerRef.current) {
      setTimeout(() => {
        setPollTriggerRef.current((prev) => prev + 1);
      }, 8000);
    }
  }, [stopRecording, liveSOAPNote, liveTranscript]);

  const onDelete = useCallback(async () => {
    try {
      const response = await blinkscribeapi.post(
        `/v1/recording/streaming/cancel?stream_filename=${streamNameRef.current}`
      );
      if (response.status === 200) {
        if (isManualRecordStartRef.current) {
          blockerRef.current = true;
          if (stopManualRecordingRef.current) {
            stopManualRecordingRef.current();
          }
        }
        stopRecording();

        if (showToastRef.current) {
          showToastRef.current("Recording Cancelled Successfully", "info");
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      blockerRef.current = false;
    }
  }, [stopRecording]);

  const startRecording = useCallback(async (data, token, combineStatus, latestApp) => {
    pausedRef.current = false;
    isManualStopRef.current = false;
    isManualRecordStartRef.current = false;
    usedPauseRef.current = false;

    try {
      console.time("⏱ [3] startRecording TOTAL");
      console.time("⏱ [3a] /auth/me");
      const response = await blinkscribeapi.get("/auth/me");
      console.timeEnd("⏱ [3a] /auth/me");
      userRef.current = response;
      patientDataRef.current = data;

      const filenamee = crypto.randomUUID();
      streamNameRef.current = filenamee;

      const notes = "Sample notes";
      const tags = "Sample tags";
      let patientname = `${data?.firstname} ${data?.lastname}`;
      const practiseid = response?.config?.practiceid;
      let departmentid = response?.config?.departmentid;
      const appointmentid = String(data?.appointmentid || "").trim();

      // 🔥 Pre-warm patient context in Redis (fire-and-forget)
      // This gives the backend a head start to cache patient history
      // before the LiveScribe WebSocket session starts.
      try {
        blinkscribeapi.post("/v1/recording/pre-warm", {
          practiceid: practiseid,
          patientid: String(data?.patientid || ""),
          departmentid: departmentid,
        }).then((res) => {
          console.log("[LiveScribe] Pre-warm response:", res?.data);
        }).catch((err) => {
          console.warn("[LiveScribe] Pre-warm failed (non-blocking):", err?.message);
        });
        console.log("[LiveScribe] Pre-warm request fired for patient:", data?.patientid);
      } catch (e) {
        console.warn("[LiveScribe] Pre-warm skipped:", e?.message);
      }

      combineStatusRef.current = combineStatus;
      latestAppRef.current = latestApp;

      // Update visit status to "Processing" when recording starts (fire-and-forget)
      if (data?.patientid && appointmentid) {
        const authToken = localStorage.getItem("auth");  // hub token key (grafted code used care-sandbox's "token")
        fetch(`/v2/ag/v1/${practiseid}/patient/${data.patientid}/orders/confirm`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
          body: JSON.stringify({
            patient_name: patientname, createdby: response?.username || "",
            orderdate: new Date().toISOString().split("T")[0],
            tov: "", lov: 0, diagnosis: [],
            appointmentid: parseInt(appointmentid) || 0,
            token_no: 0, confirm_status: "E", update_status: "C",
          }),
        }).catch(() => {});
      }

      let filenameobj = {
        practiceid: practiseid,
        departmentid: departmentid,
        patientid: data?.patientid,
        currentUser: "PRO",
        fileExtension: "txt",
        appointmentid: appointmentid,
      };

      if (combineStatus) {
        filenameobj.rowid = latestApp?.id;
        filenameobj.filename = latestApp?.audiolink;
        filenameobj.category = "combine";
      }

      const generatedfilename = generateFilename(filenameobj);

      let queryString = `token=${token}&stream_filename=${filenamee}&generated_file_name=${generatedfilename}&practiceid=${practiseid}&departmentid=${departmentid}&patientid=${data?.patientid}&appointmentid=${appointmentid}&template_name=&notes=${encodeURIComponent(notes)}&tags=${encodeURIComponent(tags)}&patientname=${encodeURIComponent(patientname)}`;

      if (combineStatus) {
        streamFilenameRef.current = latestApp?.audiolink;
        queryString += `&category=combine&filename=${latestApp?.audiolink}&rowid=${latestApp?.id}`;
      }

      // Fix 3: Auto-reconnect WebSocket with exponential backoff
      const MAX_RETRIES = 5;
      const RETRY_DELAY_MS = 2000;

      const connectWebSocket = (qs, retryCount = 0) => {
        const socket = new WebSocket(
          `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}/v2/bs/v1/recording/live-scribe?${qs}`
        );

        socket.onopen = () => {
          console.log(`[LiveScribe] WebSocket connected${retryCount > 0 ? ` (reconnect #${retryCount})` : ""}`);
          setLiveScribeHealthy(true);
          liveScribeActivityRef.current = Date.now();
          // Flush any buffered chunks
          while (pendingChunksRef.current.length > 0) {
            const chunk = pendingChunksRef.current.shift();
            socket.send(chunk);
          }
          // Fix 5: Start heartbeat
          if (heartbeatRef.current) clearInterval(heartbeatRef.current);
          heartbeatRef.current = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({ type: "heartbeat" }));
            }
          }, 15000);
        };

        // Handle incoming events from Gemini Live backend
        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            // --- Session resumed after server restart ---
            if (data.type === "session_resumed") {
              console.log(`[LiveScribe] Session resumed with ${data.transcript_length} chars of prior transcript`);
              if (showToastRef.current) {
                showToastRef.current("Recording reconnected — context restored", "info");
              }
              return;
            }

            // --- Main SOAP update (log_encounter_data tool) ---
            if (data.type === "soap_update") {
              console.log(`[LiveScribe] SOAP update #${data.tool_call_number}`, data);
              liveScribeActivityRef.current = Date.now();
              setLiveScribeHealthy(true);
              if (data.soap_note) setLiveSOAPNote(data.soap_note);
              if (data.transcript_segment) {
                setLiveTranscript((prev) => prev + data.transcript_segment + "\n");
              }
              if (data.suggested_icd10) setSuggestedICD10(data.suggested_icd10);
              if (data.suggested_orders) setSuggestedOrders(data.suggested_orders);
              if (data.careiq_alerts?.length > 0) {
                setCareIQAlerts((prev) => [...prev, ...data.careiq_alerts]);
              }
              setToolCallCount(data.tool_call_number);
              eventBus.emit("liveScribeUpdate", data);
            }

            // --- Granular tool: suggest_diagnosis ---
            if (data.type === "suggest_diagnosis") {
              console.log(`[LiveScribe] 🏥 Suggested Dx: ${data.icd10_code} ${data.description}`);
              setSuggestedICD10((prev) => {
                const entry = data.icd10_code
                  ? `${data.icd10_code} - ${data.description}`
                  : data.description;
                return prev.includes(entry) ? prev : [...prev, entry];
              });
              setToolCallCount(data.tool_call_number);
              eventBus.emit("liveScribeDiagnosis", data);
            }

            // --- Granular tool: set_level_of_visit ---
            if (data.type === "set_level_of_visit") {
              console.log(`[LiveScribe] 📊 LoV: ${data.level} (${data.reasoning})`);
              setToolCallCount(data.tool_call_number);
              eventBus.emit("liveScribeLoV", data);
            }

            // --- Granular tool: add_suggested_order ---
            if (data.type === "add_suggested_order") {
              console.log(`[LiveScribe] 📋 Order: ${data.order_name} (${data.order_type})`);
              setSuggestedOrders((prev) => {
                // prev may hold full order objects (from soap_update) or strings
                // (legacy). Dedupe by order_name; keep full structured shape.
                const seen = new Set(prev.map((o) =>
                  typeof o === "string" ? o : (o?.order_name || o?.order || "")
                ).filter(Boolean));
                if (seen.has(data.order_name)) return prev;
                return [...prev, {
                  order_name: data.order_name,
                  order_type: data.order_type,
                  details: data.details,
                }];
              });
              setToolCallCount(data.tool_call_number);
              eventBus.emit("liveScribeOrder", data);
            }

            // --- Recording resilience signals from server ---
            // Backend sends these when something is degraded so the provider
            // sees a banner immediately instead of finding out at end-of-session.
            if (data.type === "recording_status") {
              console.warn(`[LiveScribe] status ${data.severity}/${data.code}: ${data.message}`);
              if (data.severity === "info") {
                // info messages clear any active warning (e.g., transcript_recovered)
                setRecordingStatus(null);
              } else {
                setRecordingStatus({
                  severity: data.severity,
                  code: data.code,
                  message: data.message,
                  ts: Date.now(),
                });
                if (showToastRef.current) {
                  showToastRef.current(data.message, data.severity === "error" ? "error" : "warning");
                }
              }
              eventBus.emit("liveScribeStatus", data);
            }
          } catch (e) {
            // Not JSON — ignore (could be binary or other)
          }
        };

        socket.onerror = (error) => {
          console.error("WebSocket error:", error);
        };

        socket.onclose = (event) => {
          // Clear heartbeat on close
          if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
          }

          if (!isManualStopRef.current && retryCount < MAX_RETRIES) {
            const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
            console.warn(`WebSocket closed (code ${event.code}). Reconnecting in ${delay}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
            setTimeout(() => {
              const newSocket = connectWebSocket(qs, retryCount + 1);
              wsRef.current = newSocket;
            }, delay);
          } else if (!isManualStopRef.current && retryCount >= MAX_RETRIES) {
            console.warn("Max WebSocket retries reached. Falling back to manual recording.");
            setLiveScribeHealthy(false);
            isManualRecordStartRef.current = true;
            if (manualRecordRef.current) manualRecordRef.current();
          } else {
            console.log("WebSocket connection closed.");
          }
        };

        return socket;
      };

      wsQueryStringRef.current = queryString;
      console.time("⏱ [3b] WebSocket connect (non-blocking)");
      const ws = connectWebSocket(queryString);
      wsRef.current = ws;
      console.timeEnd("⏱ [3b] WebSocket connect (non-blocking)");

      console.time("⏱ [3c] getUserMedia");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      console.timeEnd("⏱ [3c] getUserMedia");

      console.time("⏱ [3d] AudioWorklet load");
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      await audioContext.audioWorklet.addModule("/pcm-processor.js");
      console.timeEnd("⏱ [3d] AudioWorklet load");

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, "pcm-processor");

      // Downsample from browser's native rate to 16kHz
      const downsample = (buffer, fromRate, toRate) => {
        if (fromRate === toRate) return buffer;
        const ratio = fromRate / toRate;
        const newLength = Math.round(buffer.length / ratio);
        const result = new Float32Array(newLength);
        for (let i = 0; i < newLength; i++) {
          result[i] = buffer[Math.floor(i * ratio)];
        }
        return result;
      };

      // Convert Float32 PCM → Int16 PCM ArrayBuffer
      const toInt16 = (float32) => {
        const int16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
          int16[i] = Math.max(-32768, Math.min(32767, Math.round(float32[i] * 32767)));
        }
        return int16.buffer;
      };

      workletNode.port.onmessage = (event) => {
        if (pausedRef.current) return;
        const downsampled = downsample(event.data, audioContext.sampleRate, 16000);
        const pcm16Buffer = toInt16(downsampled);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(pcm16Buffer);
        } else {
          // Buffer PCM chunks (as ArrayBuffer) during reconnect
          pendingChunksRef.current.push(pcm16Buffer);
          if (pendingChunksRef.current.length > 60) {
            pendingChunksRef.current.shift();
            console.warn("Pending PCM buffer exceeded 60 chunks — dropping oldest");
          }
        }
      };

      source.connect(workletNode);
      // Mute worklet output (avoid feedback)
      const muteGain = audioContext.createGain();
      muteGain.gain.value = 0;
      workletNode.connect(muteGain);
      muteGain.connect(audioContext.destination);

      // Store worklet in recorderRef so stopRecording() can clean it up
      recorderRef.current = {
        audioContext,
        workletNode,
        source,
        stopRecording: (cb) => {
          workletNode.disconnect();
          source.disconnect();
          audioContext.close();
          if (cb) cb();
        },
      };


      setGlobalElapsedTime(0);
      startTimeRef.current = Date.now();
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        const timePassed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setGlobalElapsedTime(timePassed);
      }, 1000);

      setIsGlobalRecording(true);
      setIsGlobalPaused(false);
      setLiveScribeHealthy(true);

      // Monitor LiveScribe activity — mark unhealthy if no WS messages for 90s
      liveScribeActivityRef.current = Date.now();
      const liveCheckInterval = setInterval(() => {
        if (!isManualStopRef.current && liveScribeActivityRef.current) {
          const elapsed = Date.now() - liveScribeActivityRef.current;
          if (elapsed > 90000 && wsRef.current?.readyState !== WebSocket.OPEN) {
            setLiveScribeHealthy(false);
          }
        }
      }, 15000);

      // Long-recording guard — every 45 min of continuous recording, prompt
      // the provider to confirm they're still recording. Prevents runaway
      // recordings if a session was forgotten / left running accidentally.
      lastLongPromptAtRef.current = Date.now();
      const LONG_RECORDING_INTERVAL_MS = 45 * 60 * 1000;
      const longRecordingCheckInterval = setInterval(() => {
        if (isManualStopRef.current) return;
        const now = Date.now();
        if (now - lastLongPromptAtRef.current >= LONG_RECORDING_INTERVAL_MS) {
          lastLongPromptAtRef.current = now;
          setShowLongRecordingPrompt(true);
        }
      }, 60000); // poll every minute

      // Clean up on stop
      const origStop = recorderRef.current?.stopRecording;
      if (recorderRef.current) {
        const wrapped = (cb) => {
          clearInterval(liveCheckInterval);
          clearInterval(longRecordingCheckInterval);
          setShowLongRecordingPrompt(false);
          setRecordingStatus(null);
          origStop?.(cb);
        };
        recorderRef.current.stopRecording = wrapped;
      }
      setPatientInfo({
        firstname: data?.firstname,
        lastname: data?.lastname,
        patientid: data?.patientid,
      });
      console.timeEnd("⏱ [3] startRecording TOTAL");

      return { success: true, streamfilename: filenamee };
    } catch (error) {
      console.error("Error starting recording:", error);
      return { success: false, error };
    }
  }, []);

  const submitAudioBlobManually = useCallback((blob) => {
    if (blob && blob.type.startsWith("audio/")) {
      if (blockerRef.current) return;

      const data = patientDataRef.current;
      const user = userRef.current;

      let obj = {
        file: blob,
        practiceid: user?.config?.practiceid,
        departmentid: user?.config?.departmentid,
        patientid: data?.patientid,
        notes: "testing data",
        tags: "tagsdata",
        patientname: `${data?.firstname} ${data?.lastname}`,
        role: "PRO",
        filename: streamNameRef.current,
        streamingfilename: streamFilenameRef.current,
        appointmentid: data?.appointmentid,
        userid: user?.id,
        templatename: "",
      };

      if (combineStatusRef.current) {
        obj.rowid = latestAppRef.current?.id;
        obj.category = "combine";
      }

      saveAudioInIndexDB(obj);
    }
  }, []);

  // Register manual recording hooks from SideFrame
  const registerManualRecording = useCallback(({ manualrecord, stopmanualRecording, pausemanualRecording, resumemanualRecording }) => {
    manualRecordRef.current = manualrecord;
    stopManualRecordingRef.current = stopmanualRecording;
    pauseManualRecordingRef.current = pausemanualRecording;
    resumeManualRecordingRef.current = resumemanualRecording;
  }, []);

  // Reset all recording/processing state — call when switching patients
  const clearLastNote = useCallback(() => {
    setLastSOAPNote(null);
    setLastTranscript("");
    setLastNotePatientId(null);
    setIsProcessing(false);
    setProcessingStage("");
    setProcessingPercent(0);
    setLiveSOAPNote(null);
    setLiveTranscript("");
    setSuggestedICD10([]);
    setSuggestedOrders([]);
    setCareIQAlerts([]);
    setToolCallCount(0);
  }, []);

  const value = {
    // UI State
    isGlobalRecording,
    setIsGlobalRecording,
    isGlobalPaused,
    setIsGlobalPaused,
    globalElapsedTime,
    setGlobalElapsedTime,
    patientInfo,
    setPatientInfo,
    showFloatingPopup,
    setShowFloatingPopup,
    isOnRecordingPage,
    setIsOnRecordingPage,

    // Recording infrastructure refs
    wsRef,
    recorderRef,
    audioStreamRef,
    pausedRef,
    usedPauseRef,
    isManualStopRef,
    isManualRecordStartRef,
    blockerRef,
    combineStatusRef,
    latestAppRef,
    streamNameRef,
    streamFilenameRef,
    userRef,
    patientDataRef,

    // Core functions
    startRecording,
    stopRecording,
    togglePause,
    onSave,
    onDelete,
    submitAudioBlobManually,
    startTimer,
    stopTimer,

    // Registration
    registerManualRecording,
    showToastRef,
    setPollTriggerRef,
    clearLastNote,

    // Live Scribe state (Passive Scribe)
    liveSOAPNote,
    liveTranscript,
    suggestedICD10,
    suggestedOrders,
    careIQAlerts,
    toolCallCount,
    liveScribeHealthy,

    // Server-pushed recording resilience signals + long-recording prompt
    recordingStatus,
    setRecordingStatus,
    showLongRecordingPrompt,
    // Provider clicked "Yes, continue" — close the modal and reset the 45-min timer
    confirmStillRecording: () => {
      lastLongPromptAtRef.current = Date.now();
      setShowLongRecordingPrompt(false);
    },
    // Provider clicked "Stop" in the modal — close the modal (caller should also call stopRecording)
    dismissLongRecordingPrompt: () => setShowLongRecordingPrompt(false),

    // Post-recording processing state
    isProcessing,
    processingStage,
    processingPercent,
    lastSOAPNote,
    lastTranscript,
    lastNotePatientId,
  };

  return (
    <RecordingContext.Provider value={value}>
      {children}
    </RecordingContext.Provider>
  );
};

export default RecordingContext;
